/**
 * Fine-Grained Permissions Service
 * 
 * Provides fine-grained permission checking for Enterprise customers
 * while maintaining backward compatibility with system roles (user, manager, admin)
 * 
 * @module permissionService
 */

import { UserWithOrganizationRoles, getUserSystemRoleForOrganization } from '@/utils/roleHelpers';
import { isEnterprisePlan } from '@/utils/enterpriseSecurity';
import { db } from '@/firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// ============================================================================
// Types
// ============================================================================

export interface Permission {
  resource: string;
  action: string | string[];
  conditions?: {
    organizationId?: 'self' | 'all' | string[];
    locationId?: 'self' | 'all' | string[];
    resourceOwner?: 'self' | 'all';
    [key: string]: any;
  };
}

export interface CustomRole {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  permissions: Permission[];
  createdBy: string;
  createdAt: any;
  updatedAt: any;
}

export interface PermissionContext {
  organizationId: string;
  locationId?: string;
  resourceOwner?: string;
  [key: string]: any;
}

// ============================================================================
// Resource and Action Constants
// ============================================================================

export const RESOURCES = {
  ASSETS: 'assets',
  TASKS: 'tasks',
  AUDITS: 'audits',
  CHECKLISTS: 'checklists',
  FORMS: 'forms',
  DOCUMENTS: 'documents',
  USERS: 'users',
  ORGANIZATIONS: 'organizations',
  LOCATIONS: 'locations',
  COMPLIANCE_TEMPLATES: 'compliance_templates',
  AGREEMENTS: 'agreements',
  POLICIES: 'policies',
  MESSAGES: 'messages',
  API_KEYS: 'api_keys',
  SETTINGS: 'settings',
  REPORTS: 'reports',
  ENCRYPTION_KEYS: 'encryption_keys',
} as const;

export const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  IMPORT: 'import',
  ASSIGN: 'assign',
  APPROVE: 'approve',
  REVOKE: 'revoke',
  MANAGE: 'manage',
  ENCRYPT: 'encrypt',
  DECRYPT: 'decrypt',
  ROTATE_KEY: 'rotate_key',
  REVOKE_KEY: 'revoke_key',
} as const;

// ============================================================================
// System Role Permission Mappings
// ============================================================================

/**
 * Get permissions for a system role
 * These are the default permissions that map to system roles
 */
function getSystemRolePermissions(systemRole: string | undefined): Permission[] {
  if (!systemRole) {
    return getUserPermissions();
  }

  switch (systemRole.toLowerCase()) {
    case 'admin':
      return getAdminPermissions();
    case 'manager':
      return getManagerPermissions();
    case 'user':
    default:
      return getUserPermissions();
  }
}

/**
 * Admin permissions - Full access within organization
 */
function getAdminPermissions(): Permission[] {
  return [
    {
      resource: '*',
      action: '*',
      conditions: {
        organizationId: 'self',
      },
    },
  ];
}

/**
 * Manager permissions - Can manage users (except admins) and locations
 */
function getManagerPermissions(): Permission[] {
  return [
    // Assets
    {
      resource: RESOURCES.ASSETS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE],
      conditions: { locationId: 'self' },
    },
    // Tasks
    {
      resource: RESOURCES.TASKS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.ASSIGN],
      conditions: { locationId: 'self' },
    },
    // Audits
    {
      resource: RESOURCES.AUDITS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE],
      conditions: { locationId: 'self' },
    },
    // Checklists
    {
      resource: RESOURCES.CHECKLISTS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE],
      conditions: { locationId: 'self' },
    },
    // Forms
    {
      resource: RESOURCES.FORMS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE],
      conditions: { locationId: 'self' },
    },
    // Documents
    {
      resource: RESOURCES.DOCUMENTS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.EXPORT],
      conditions: { locationId: 'self' },
    },
    // Users (can't assign admin role)
    {
      resource: RESOURCES.USERS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.ASSIGN],
      conditions: { organizationId: 'self' },
    },
    // Locations
    {
      resource: RESOURCES.LOCATIONS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE],
      conditions: { organizationId: 'self' },
    },
    // Messages
    {
      resource: RESOURCES.MESSAGES,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE],
      conditions: { organizationId: 'self' },
    },
    // Reports
    {
      resource: RESOURCES.REPORTS,
      action: [ACTIONS.READ, ACTIONS.EXPORT],
      conditions: { organizationId: 'self' },
    },
  ];
}

/**
 * User permissions - Limited access to assigned resources
 */
function getUserPermissions(): Permission[] {
  return [
    // Assets (read only)
    {
      resource: RESOURCES.ASSETS,
      action: ACTIONS.READ,
      conditions: { locationId: 'self' },
    },
    // Tasks (read and update own tasks)
    {
      resource: RESOURCES.TASKS,
      action: [ACTIONS.READ, ACTIONS.UPDATE],
      conditions: { resourceOwner: 'self' },
    },
    // Checklists (read only)
    {
      resource: RESOURCES.CHECKLISTS,
      action: ACTIONS.READ,
      conditions: { locationId: 'self' },
    },
    // Forms (read, create, update)
    {
      resource: RESOURCES.FORMS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE],
      conditions: { locationId: 'self' },
    },
    // Documents (read, create, update own)
    {
      resource: RESOURCES.DOCUMENTS,
      action: [ACTIONS.READ, ACTIONS.CREATE, ACTIONS.UPDATE],
      conditions: { locationId: 'self' },
    },
    // Messages
    {
      resource: RESOURCES.MESSAGES,
      action: [ACTIONS.READ, ACTIONS.CREATE],
      conditions: { organizationId: 'self' },
    },
    // Reports (read only)
    {
      resource: RESOURCES.REPORTS,
      action: ACTIONS.READ,
      conditions: { organizationId: 'self' },
    },
  ];
}

// ============================================================================
// Permission Checking Logic
// ============================================================================

/**
 * Check if a permission matches the requested resource and action
 */
function matchesPermission(
  permission: Permission,
  resource: string,
  action: string,
  context: PermissionContext
): boolean {
  // Check resource match
  if (permission.resource !== '*' && permission.resource !== resource) {
    return false;
  }

  // Check action match
  const actions = Array.isArray(permission.action) ? permission.action : [permission.action];
  if (!actions.includes('*') && !actions.includes(action)) {
    return false;
  }

  // Check conditions
  if (permission.conditions) {
    return evaluateConditions(permission.conditions, context);
  }

  return true;
}

/**
 * Evaluate permission conditions
 */
function evaluateConditions(
  conditions: Permission['conditions'],
  context: PermissionContext
): boolean {
  if (!conditions) {
    return true;
  }

  // Check organizationId condition
  if (conditions.organizationId) {
    if (conditions.organizationId === 'self') {
      // User must belong to the organization
      // This is handled at the query level, so we assume it's valid if context matches
      if (!context.organizationId) {
        return false;
      }
    } else if (conditions.organizationId === 'all') {
      // Admin only - handled by system role
      // This is valid
    } else if (Array.isArray(conditions.organizationId)) {
      // Specific organizations
      if (!conditions.organizationId.includes(context.organizationId)) {
        return false;
      }
    }
  }

  // Check locationId condition
  if (conditions.locationId) {
    if (conditions.locationId === 'self') {
      // User must have access to the location
      // This is handled at the query level (user.locationIds)
      // We assume it's valid if locationId is provided in context
      if (!context.locationId) {
        return false;
      }
    } else if (conditions.locationId === 'all') {
      // All locations in organization
      // Valid
    } else if (Array.isArray(conditions.locationId)) {
      // Specific locations
      if (!context.locationId || !conditions.locationId.includes(context.locationId)) {
        return false;
      }
    }
  }

  // Check resourceOwner condition
  if (conditions.resourceOwner) {
    if (conditions.resourceOwner === 'self') {
      // Resource must be owned by the user
      if (!context.resourceOwner || context.resourceOwner !== context.userId) {
        return false;
      }
    }
    // 'all' means no ownership restriction
  }

  // Check custom conditions
  for (const [key, value] of Object.entries(conditions)) {
    if (key === 'organizationId' || key === 'locationId' || key === 'resourceOwner') {
      continue; // Already checked
    }

    // Custom condition check
    if (context[key] !== value) {
      return false;
    }
  }

  return true;
}

// ============================================================================
// Custom Role Management (Enterprise Only)
// ============================================================================

/**
 * Get custom roles for a user in an organization
 */
async function getUserCustomRoles(
  user: UserWithOrganizationRoles,
  organizationId: string
): Promise<CustomRole[]> {
  // Check if organization has Enterprise plan
  const isEnterprise = await isEnterprisePlan(organizationId);
  if (!isEnterprise) {
    return [];
  }

  // Get user's custom role IDs from user document
  // This would be stored in user.organizationRoles[organizationId].customRoleIds
  const userOrgRoles = user.organizationRoles?.[organizationId];
  const customRoleIds = (userOrgRoles as any)?.customRoleIds || [];

  if (customRoleIds.length === 0) {
    return [];
  }

  // Fetch custom roles from Firestore
  const customRoles: CustomRole[] = [];
  for (const roleId of customRoleIds) {
    try {
      const roleDoc = await getDoc(doc(db, 'custom_roles', roleId));
      if (roleDoc.exists()) {
        const roleData = roleDoc.data() as Omit<CustomRole, 'id'>;
        // Verify role belongs to the organization
        if (roleData.organizationId === organizationId) {
          customRoles.push({ id: roleDoc.id, ...roleData } as CustomRole);
        }
      }
    } catch (error) {
      console.error(`Error fetching custom role ${roleId}:`, error);
    }
  }

  return customRoles;
}

// ============================================================================
// Main Permission Check Function
// ============================================================================

/**
 * Check if user has permission to perform an action on a resource
 * 
 * This function implements a hybrid approach:
 * 1. First checks fine-grained permissions (custom roles for Enterprise)
 * 2. Falls back to system role permissions (backward compatible)
 * 
 * @param user - User object
 * @param resource - Resource type (e.g., 'tasks', 'assets')
 * @param action - Action to perform (e.g., 'read', 'delete')
 * @param context - Permission context (organizationId, locationId, etc.)
 * @returns Promise<boolean> - True if user has permission
 */
export async function hasPermission(
  user: UserWithOrganizationRoles | null | undefined,
  resource: string,
  action: string,
  context: PermissionContext
): Promise<boolean> {
  if (!user || !context.organizationId) {
    return false;
  }

  // Add userId to context for resourceOwner checks
  context.userId = user.id;

  // 1. Check custom role permissions (Enterprise only)
  const customRoles = await getUserCustomRoles(user, context.organizationId);
  for (const customRole of customRoles) {
    for (const permission of customRole.permissions) {
      if (matchesPermission(permission, resource, action, context)) {
        return true;
      }
    }
  }

  // 2. Check system role permissions (backward compatible)
  const systemRole = getUserSystemRoleForOrganization(user, context.organizationId);
  const systemPermissions = getSystemRolePermissions(systemRole);

  for (const permission of systemPermissions) {
    if (matchesPermission(permission, resource, action, context)) {
      return true;
    }
  }

  return false;
}

/**
 * Synchronous version of hasPermission (for cases where custom roles aren't needed)
 * Uses only system role permissions
 * 
 * @param user - User object
 * @param resource - Resource type
 * @param action - Action to perform
 * @param context - Permission context
 * @returns boolean - True if user has permission
 */
export function hasPermissionSync(
  user: UserWithOrganizationRoles | null | undefined,
  resource: string,
  action: string,
  context: PermissionContext
): boolean {
  if (!user || !context.organizationId) {
    return false;
  }

  // Add userId to context
  context.userId = user.id;

  // Check system role permissions only
  const systemRole = getUserSystemRoleForOrganization(user, context.organizationId);
  const systemPermissions = getSystemRolePermissions(systemRole);

  for (const permission of systemPermissions) {
    if (matchesPermission(permission, resource, action, context)) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// Permission Caching (Optional - for performance)
// ============================================================================

interface PermissionCache {
  [key: string]: {
    result: boolean;
    timestamp: number;
  };
}

const permissionCache: PermissionCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached permission result
 */
function getCachedPermission(cacheKey: string): boolean | null {
  const cached = permissionCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }
  return null;
}

/**
 * Cache permission result
 */
function setCachedPermission(cacheKey: string, result: boolean): void {
  permissionCache[cacheKey] = {
    result,
    timestamp: Date.now(),
  };
}

/**
 * Generate cache key for permission check
 */
function getCacheKey(
  userId: string,
  organizationId: string,
  resource: string,
  action: string
): string {
  return `${userId}:${organizationId}:${resource}:${action}`;
}

/**
 * Cached version of hasPermission
 */
export async function hasPermissionCached(
  user: UserWithOrganizationRoles | null | undefined,
  resource: string,
  action: string,
  context: PermissionContext
): Promise<boolean> {
  if (!user || !context.organizationId) {
    return false;
  }

  const cacheKey = getCacheKey(user.id, context.organizationId, resource, action);
  const cached = getCachedPermission(cacheKey);
  if (cached !== null) {
    return cached;
  }

  const result = await hasPermission(user, resource, action, context);
  setCachedPermission(cacheKey, result);
  return result;
}

/**
 * Clear permission cache for a user
 */
export function clearPermissionCache(userId?: string, organizationId?: string): void {
  if (userId && organizationId) {
    // Clear specific user's cache
    const prefix = `${userId}:${organizationId}:`;
    Object.keys(permissionCache).forEach(key => {
      if (key.startsWith(prefix)) {
        delete permissionCache[key];
      }
    });
  } else {
    // Clear all cache
    Object.keys(permissionCache).forEach(key => {
      delete permissionCache[key];
    });
  }
}

// ============================================================================
// Export
// ============================================================================

export const permissionService = {
  hasPermission,
  hasPermissionSync,
  hasPermissionCached,
  clearPermissionCache,
  RESOURCES,
  ACTIONS,
};

